import { useRef, useEffect } from "react";

/**
 * Lightweight floating particles background — renders across ALL pages.
 * Adapts to light/dark mode automatically.
 */
const FloatingParticles = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Уважаем prefers-reduced-motion — тогда анимацию не крутим вовсе.
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let animId = 0;
    let running = false;
    let t = 0;
    let lastPaint = 0;
    const frameInterval = 1000 / 30;
    // Кэшируем размеры (в CSS-пикселях), чтобы НЕ дёргать getBoundingClientRect
    // каждый кадр — иначе это форс-лейаут (layout thrashing) на каждый rAF.
    let w = 0;
    let h = 0;

    const particles: { x: number; y: number; r: number; speed: number; phase: number }[] = [];
    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random(),
        y: Math.random(),
        r: 0.5 + Math.random() * 1.5,
        speed: 0.0002 + Math.random() * 0.0005,
        phase: Math.random() * Math.PI * 2,
      });
    }

    const resize = () => {
      // Канвас fixed на весь экран — считаем от вьюпорта, а не от растянутого
      // элемента. DPR ограничиваем 2 (на retina 3x канвас был бы вчетверо дороже).
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = (now: number) => {
      const elapsed = now - lastPaint;
      if (elapsed < frameInterval) {
        animId = requestAnimationFrame(draw);
        return;
      }

      lastPaint = now - (elapsed % frameInterval);
      const isDark = document.documentElement.classList.contains("dark");
      t += Math.min(elapsed, 100) * 0.00048;

      ctx.clearRect(0, 0, w, h);

      for (const p of particles) {
        const px = (p.x + Math.sin(t * 0.5 + p.phase) * 0.02) * w;
        const py = ((p.y + t * p.speed) % 1) * h;
        const alpha = isDark ? 0.108 + Math.sin(t + p.phase) * 0.072 : 0.054 + Math.sin(t + p.phase) * 0.036;

        ctx.beginPath();
        ctx.arc(px, py, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 83, 49, ${Math.max(0, alpha)})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };

    const start = () => {
      if (running || reduceMotion) return;
      running = true;
      lastPaint = performance.now();
      animId = requestAnimationFrame(draw);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(animId);
    };

    // Не жжём кадры, когда вкладка скрыта.
    const onVisibility = () => (document.hidden ? stop() : start());
    document.addEventListener("visibilitychange", onVisibility);

    start();

    return () => {
      stop();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed-background-canvas fixed inset-0 h-full w-full pointer-events-none z-0"
      style={{ opacity: 0.54 }}
    />
  );
};

export default FloatingParticles;
